import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';

interface Props {
  children: React.ReactNode;
}

/**
 * Decodifica el JWT y devuelve el valor del claim aal_passkey (custom
 * token hook). Fail-safe: si el JWT es inválido o no tiene el claim,
 * retorna false (= no verificado por passkey).
 */
function readAalPasskeyClaim(jwt: string): boolean {
  try {
    const [, payloadB64] = jwt.split('.');
    if (!payloadB64) return false;
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(padded + '==='.slice((padded.length + 3) % 4));
    const payload = JSON.parse(decoded) as { aal_passkey?: boolean };
    return payload.aal_passkey === true;
  } catch {
    return false;
  }
}

/**
 * RequireMFAVerified · guard global de la app autenticada.
 *
 * Lógica clásica:
 *  - User SIN MFA enrolled → pasa transparente
 *  - User CON MFA enrolled + aal2 → pasa (factor ya verificado)
 *  - User CON MFA enrolled + aal1 → redirect a /auth/mfa-challenge
 *
 * Se aplica envolviendo AppLayout (toda la app autenticada). Las rutas
 * /auth/* viven afuera de este guard para evitar loops.
 *
 * `getAuthenticatorAssuranceLevel()` devuelve:
 *  - currentLevel: 'aal1' | 'aal2' | null
 *  - nextLevel: 'aal1' | 'aal2' (lo que puede alcanzar)
 *  - Si nextLevel='aal2' significa que TIENE factor verified disponible.
 */
export function RequireMFAVerified({ children }: Props) {
  const { session, loading } = useSupabase();
  const location = useLocation();

  const [checked, setChecked] = useState(false);
  const [needsChallenge, setNeedsChallenge] = useState(false);
  const lastCheckedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setChecked(true);
      return;
    }

    // Solo re-chequear si el access_token cambió (refresh o nuevo login).
    // Sin esto el effect podía dispararse en loop por re-renders.
    if (lastCheckedTokenRef.current === session.access_token) return;
    lastCheckedTokenRef.current = session.access_token;

    void (async () => {
      const { data, error } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (error || !data) {
        // Falla del SDK → fail-open (no bloqueamos navegación)
        // eslint-disable-next-line no-console
        console.warn('[RequireMFAVerified] No se pudo leer AAL', error);
        setChecked(true);
        return;
      }

      // nextLevel='aal2' significa que el user tiene factor TOTP/SMS
      // verified disponible. currentLevel='aal2' significa que esta sesión
      // ya pasó por TOTP/SMS challenge.
      const hasFactorEnrolled = data.nextLevel === 'aal2';
      const verifiedViaSupabaseMfa = data.currentLevel === 'aal2';

      // También aceptamos passkey propio como factor verificado: el hook
      // custom_access_token_hook agrega claim aal_passkey:true al JWT
      // cuando la sesión pasó por core-passkey-auth-verify. Esto cierra
      // el ciclo "login → mfa-challenge → biometría → app" sin tocar
      // el AAL nativo de Supabase.
      const verifiedViaPasskey = readAalPasskeyClaim(session.access_token);

      const verified = verifiedViaSupabaseMfa || verifiedViaPasskey;

      setNeedsChallenge(hasFactorEnrolled && !verified);
      setChecked(true);
    })();
  }, [session, loading]);

  if (loading || !checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-fg-muted text-body-lg">Verificando sesión…</div>
      </div>
    );
  }

  if (needsChallenge) {
    return (
      <Navigate
        to="/auth/mfa-challenge"
        replace
        state={{ from: location }}
      />
    );
  }

  return <>{children}</>;
}
